// Web Push for the admin app (2026-10-01): register the admin service worker,
// turn push on for this device, and forget the device on logout.
//
// Browser objects come in as arguments (`navigator.serviceWorker`,
// `Notification`, `pushApi`) so every path here runs in tests with fakes.
import type { PushPlatform, PushSubscribeBody } from '@/lib/api';
import { base64UrlToUint8Array, isVapidPublicKey, sameKey } from './vapidKey';

export const ADMIN_SW_URL = '/admin-sw.js';
/** ⚠️ The admin only. The customer pages never get this worker. */
export const ADMIN_SW_SCOPE = '/admin/';

// ─── The slices of the browser types this file uses ─────────────────────────

export interface SubLike {
  readonly endpoint: string;
  readonly options?: { readonly applicationServerKey: ArrayBuffer | null };
  toJSON(): PushSubscriptionJSON;
  unsubscribe(): Promise<boolean>;
}
export interface PushManagerLike {
  getSubscription(): Promise<SubLike | null>;
  subscribe(options: PushSubscriptionOptionsInit): Promise<SubLike>;
}
export interface WorkerLike extends EventTarget {
  readonly state: ServiceWorkerState;
}
export interface RegLike {
  readonly active: WorkerLike | null;
  readonly installing: WorkerLike | null;
  readonly waiting: WorkerLike | null;
  readonly pushManager: PushManagerLike;
}
export interface SwContainerLike {
  register(url: string, options: RegistrationOptions): Promise<RegLike>;
  getRegistration(clientURL?: string): Promise<RegLike | undefined>;
}

// ─── Registration ───────────────────────────────────────────────────────────

const registrations = new WeakMap<SwContainerLike, Promise<RegLike>>();

/**
 * Register /admin-sw.js for /admin/ — once per page load, however many admin
 * pages mount (each page renders its own AdminLayout). Null when the browser
 * has no service workers. A failed attempt is forgotten so the next call retries.
 */
export function registerAdminServiceWorker(sw: SwContainerLike | undefined): Promise<RegLike | null> {
  if (!sw) return Promise.resolve(null);
  let p = registrations.get(sw);
  if (!p) {
    p = sw.register(ADMIN_SW_URL, { scope: ADMIN_SW_SCOPE });
    registrations.set(sw, p);
    p.catch(() => registrations.delete(sw));
  }
  return p;
}

/**
 * Resolve once the registration has an ACTIVE worker (pushManager.subscribe
 * refuses before that).
 *
 * ⚠️ NOT `navigator.serviceWorker.ready`. That waits for a registration whose
 * scope covers the CURRENT page — and the dashboard is /admin, which the
 * /admin/ scope does not cover. On the very page the card lives on, `ready`
 * would never resolve and the button would spin forever.
 */
export function whenActive(reg: RegLike, timeoutMs = 20_000): Promise<RegLike> {
  if (reg.active) return Promise.resolve(reg);
  const worker = reg.installing ?? reg.waiting;
  if (!worker) return Promise.reject(new Error('The app’s background worker did not start.'));
  return new Promise<RegLike>((resolve, reject) => {
    const timer = setTimeout(() => {
      worker.removeEventListener('statechange', onChange);
      reject(new Error('The app’s background worker did not start in time.'));
    }, timeoutMs);
    function onChange() {
      if (worker!.state === 'activated') {
        clearTimeout(timer);
        worker!.removeEventListener('statechange', onChange);
        resolve(reg);
      } else if (worker!.state === 'redundant') {
        clearTimeout(timer);
        worker!.removeEventListener('statechange', onChange);
        reject(new Error('The app’s background worker did not start (install failed).'));
      }
    }
    worker.addEventListener('statechange', onChange);
  });
}

// ─── Subscriptions ──────────────────────────────────────────────────────────

/** PushSubscription.toJSON() → the POST /push/subscribe body. */
export function subscriptionBody(json: PushSubscriptionJSON, platform: PushPlatform): PushSubscribeBody {
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) throw new Error('The browser returned an incomplete push subscription.');
  return { endpoint: json.endpoint, keys: { p256dh, auth }, platform };
}

/** This device's subscription for the admin worker, if any. */
export async function currentSubscription(sw: SwContainerLike | undefined): Promise<SubLike | null> {
  if (!sw) return null;
  const reg = await sw.getRegistration(ADMIN_SW_SCOPE);
  return reg ? reg.pushManager.getSubscription() : null;
}

export interface PushApiLike {
  key(): Promise<{ publicKey: string }>;
  subscribe(body: PushSubscribeBody): Promise<unknown>;
}

export interface TurnOnDeps {
  notification: { requestPermission(): Promise<NotificationPermission> };
  serviceWorker: SwContainerLike;
  api: PushApiLike;
  platform: PushPlatform;
  /** Somebody is logged in right now. Read at the tap AND before subscribing. */
  isSignedIn: () => boolean;
}

export type TurnOnResult =
  | { status: 'on' }
  | { status: 'blocked' }
  | { status: 'dismissed' }
  | { status: 'signed-out' }
  | { status: 'error'; message: string };

const statusOf = (e: unknown): number | undefined => (e as { status?: number } | null)?.status;
const reasonOf = (e: unknown): string => (e instanceof Error && e.message ? e.message : String(e));
const NOT_SET_UP = 'Push is not set up on the server yet. Try again once it is.';

/**
 * The « Turn on notifications » tap.
 *
 * ⚠️ CALL IT DIRECTLY FROM THE CLICK HANDLER, AND KEEP requestPermission FIRST.
 * iPhone shows the permission prompt only when it is asked for inside the tap;
 * a single await before it (the key, the registration) and the tap is gone.
 */
export async function turnOnPush(d: TurnOnDeps): Promise<TurnOnResult> {
  // Never for nobody: a logged-out device must not start receiving alerts.
  if (!d.isSignedIn()) return { status: 'signed-out' };

  const permission = await d.notification.requestPermission();
  if (permission === 'denied') return { status: 'blocked' };
  if (permission !== 'granted') return { status: 'dismissed' };

  let reg: RegLike;
  try {
    const r = await registerAdminServiceWorker(d.serviceWorker);
    if (!r) return { status: 'error', message: 'This browser cannot run the app’s background worker.' };
    reg = await whenActive(r);
  } catch (e) {
    return { status: 'error', message: `The app’s background worker did not start (${reasonOf(e)}). Reload the page and try again.` };
  }

  let key: Uint8Array<ArrayBuffer>;
  try {
    key = base64UrlToUint8Array((await d.api.key()).publicKey ?? '');
  } catch (e) {
    // 404 = no /push route yet; 503 = the server has no VAPID keys yet.
    if (statusOf(e) === 404 || statusOf(e) === 503) return { status: 'error', message: NOT_SET_UP };
    return { status: 'error', message: `Could not get the push key from the server (${reasonOf(e)}). Try again.` };
  }
  if (!isVapidPublicKey(key)) return { status: 'error', message: NOT_SET_UP };

  // Logged out while the prompt was up (another tab): stop before subscribing.
  if (!d.isSignedIn()) return { status: 'signed-out' };

  let sub: SubLike;
  try {
    let existing = await reg.pushManager.getSubscription();
    if (existing && !sameKey(existing.options?.applicationServerKey, key)) {
      // Made with an older server key: the browser will not subscribe with a
      // new key until this one is dropped.
      await existing.unsubscribe();
      existing = null;
    }
    sub = existing ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  } catch (e) {
    return { status: 'error', message: `This device refused the push subscription (${reasonOf(e)}). Try again.` };
  }

  try {
    await d.api.subscribe(subscriptionBody(sub.toJSON(), d.platform));
  } catch (e) {
    if (statusOf(e) === 404) return { status: 'error', message: NOT_SET_UP };
    return { status: 'error', message: `Could not save this device on the server (${reasonOf(e)}). Try again.` };
  }
  return { status: 'on' };
}

/** Send this device's existing subscription to the server again (upsert). */
export async function saveSubscription(sub: SubLike, api: Pick<PushApiLike, 'subscribe'>, platform: PushPlatform): Promise<void> {
  await api.subscribe(subscriptionBody(sub.toJSON(), platform));
}

// ─── Logout ─────────────────────────────────────────────────────────────────

export type ForgetResult = 'none' | 'forgotten' | 'timeout' | 'failed';

/**
 * Logging out stops push to this device (owner, 2026-10-01: « one time I was
 * logged out and I still got a notification — fix this »).
 *
 * 1. DELETE /push/subscribe — first, while the session is still valid;
 * 2. drop the browser's own subscription — even if step 1 failed: the endpoint
 *    is then dead, and the server deletes its row on the next 404/410.
 *
 * ⚠️ NEVER BLOCKS THE LOGOUT: bounded by `timeoutMs`, and it never throws.
 * Whatever happens here, the caller signs out right after.
 */
export async function forgetPushDevice(d: {
  serviceWorker: SwContainerLike | undefined;
  api: { unsubscribe(endpoint: string): Promise<unknown> };
  timeoutMs?: number;
}): Promise<ForgetResult> {
  const work = (async (): Promise<ForgetResult> => {
    const sub = await currentSubscription(d.serviceWorker);
    if (!sub) return 'none';
    try { await d.api.unsubscribe(sub.endpoint); } catch { /* the browser side below still kills it */ }
    await sub.unsubscribe();
    return 'forgotten';
  })().catch((): ForgetResult => 'failed');

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<ForgetResult>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), d.timeoutMs ?? 4000);
  });
  try {
    return await Promise.race([work, timeout]);
  } finally {
    clearTimeout(timer);
  }
}
